/**
 * PromSurvey — Patient-Reported Outcome Measure (PROM) submission UI.
 * Iter 68.
 *
 * Renders any of the in-platform surveys (Oswestry, NDI, QuickDASH, VAS) as a
 * one-question-per-card form. Auto-saves nothing until submit. Auto-scores
 * server-side; we show the score + severity label after submit and offer
 * "View trend".
 *
 * Used from:
 *   • Patient self-portal (/patient/proms)
 *   • Admin patient chart (/admin/patients/:pid/proms)
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ClipboardList, ArrowLeft, TrendingUp, CheckCircle2, Loader2, Activity } from 'lucide-react';
import { toast } from 'sonner';

const BAND_COLOR = (label = '') => {
  const lower = label.toLowerCase();
  if (lower.includes('no ') || lower.includes('minimal') || lower.includes('mild')) return '#10A862';
  if (lower.includes('moderate')) return '#FFB000';
  if (lower.includes('severe')) return '#EF4444';
  if (lower.includes('complete') || lower.includes('crippled') || lower.includes('extreme')) return '#7F1D1D';
  return '#6B7280';
};

export default function PromSurvey() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { survey_key, patient_id } = useParams();
  const isStaff = user?.role === 'admin' || user?.role === 'clinician';
  const targetPid = isStaff ? patient_id : user?.user_id;

  const [surveys, setSurveys] = useState([]);
  const [schema, setSchema] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/proms/surveys').then(r => setSurveys(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!survey_key) { setSchema(null); return; }
    api.get(`/proms/surveys/${survey_key}`).then(r => setSchema(r.data)).catch(() => {});
    setAnswers({});
    setResult(null);
  }, [survey_key]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post('/proms/responses', {
        survey_key,
        answers,
        patient_id: isStaff ? targetPid : undefined,
      });
      setResult(r.data);
      toast.success(`Submitted — score ${r.data.score_pct}%`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // Survey picker (when no survey_key in URL)
  if (!survey_key) {
    return (
      <div className="p-4 md:p-8 max-w-3xl">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">PROGRESS TRACKING</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-2">Outcome surveys</h1>
        <p className="text-fg-muted mb-6 text-sm">
          Self-report scales used worldwide. Re-take any of them every {isStaff ? 'few visits' : '2–4 weeks'} to see your recovery curve.
        </p>
        <div className="grid md:grid-cols-2 gap-4" data-testid="prom-survey-grid">
          {surveys.map(s => (
            <Link key={s.key}
                  to={isStaff ? `/admin/patients/${patient_id}/proms/${s.key}` : `/patient/proms/${s.key}`}
                  data-testid={`prom-survey-${s.key}`}
                  className="bento-card hover:border-brand hover:shadow-sm transition group">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center group-hover:bg-brand/20">
                  <ClipboardList className="w-5 h-5 text-brand"/>
                </div>
                <div className="flex-1">
                  <div className="font-heading font-semibold text-base">{s.name}</div>
                  <div className="text-xs text-fg-muted">{s.scope}</div>
                  <div className="text-[11px] text-fg-muted mt-2">{s.n_items} questions · ~{Math.max(2, Math.round(s.n_items / 3))} min</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  if (!schema) return <div className="p-8">Loading survey…</div>;

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      <Link to={isStaff ? `/admin/patients/${patient_id}/proms` : '/patient/proms'}
            className="text-xs text-fg-muted inline-flex items-center gap-1 hover:text-brand mb-4">
        <ArrowLeft className="w-3 h-3"/> Back to surveys
      </Link>
      <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">SURVEY · {schema.abbrev}</div>
      <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-2" data-testid="prom-survey-title">{schema.name}</h1>
      <p className="text-fg-muted text-sm mb-6">{schema.scope}</p>

      {result ? (
        <div className="bento-card text-center" data-testid="prom-result">
          <CheckCircle2 className="w-12 h-12 mx-auto text-[#10A862] mb-3"/>
          <div className="text-xs uppercase tracking-wider text-fg-muted mb-1">Score</div>
          <div className="text-6xl font-heading font-bold">{result.score_pct}<span className="text-2xl text-fg-muted">%</span></div>
          <div className="inline-block mt-3 px-4 py-1.5 rounded-full text-sm font-semibold text-white"
               style={{ background: BAND_COLOR(result.label) }}>
            {result.label}
          </div>
          <div className="text-[11px] text-fg-muted mt-4">{result.items_answered}/{result.items_total} items answered</div>
          <div className="mt-5 flex justify-center gap-2 flex-wrap">
            <button onClick={() => { setResult(null); setAnswers({}); }} className="btn-secondary text-sm" data-testid="prom-retake">
              Take again
            </button>
            <Link to={isStaff ? `/admin/patients/${patient_id}/proms` : '/patient/proms'} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="prom-back-survey-list">
              <TrendingUp className="w-4 h-4"/> View trend
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {schema.items.map((item, idx) => {
            const isQuickDash = schema.key === 'quickdash';
            const isVas = schema.key === 'vas';
            const options = isQuickDash ? schema.options : (item.options || []);
            return (
              <div key={item.id} className="bento-card" data-testid={`prom-item-${item.id}`}>
                <div className="flex gap-3 mb-3">
                  <div className="shrink-0 w-8 h-8 rounded-full bg-brand/10 text-brand font-bold flex items-center justify-center text-sm">
                    {idx + 1}
                  </div>
                  <h3 className="font-heading font-semibold leading-snug pt-1">{item.label}</h3>
                </div>
                {isVas ? (
                  <div>
                    <div className="flex items-center justify-between text-[11px] text-fg-muted mb-1.5">
                      <span>0 · No pain</span>
                      <span className="font-bold text-brand text-base">{answers[item.id] ?? '—'}</span>
                      <span>10 · Worst</span>
                    </div>
                    <input type="range" min="0" max="10" step="1" value={answers[item.id] ?? 0}
                           onChange={(e) => setAnswers(a => ({ ...a, [item.id]: parseInt(e.target.value) }))}
                           className="w-full accent-brand"
                           data-testid={`prom-vas-${item.id}`}/>
                  </div>
                ) : isQuickDash ? (
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-1.5">
                    {options.map((opt, i) => (
                      <label key={i} className={`text-xs p-2 rounded-md border cursor-pointer text-center transition ${answers[item.id] === (i + 1) ? 'bg-brand text-white border-brand' : 'border-soft hover:border-brand'}`}>
                        <input type="radio" name={item.id} className="hidden"
                               checked={answers[item.id] === (i + 1)}
                               onChange={() => setAnswers(a => ({ ...a, [item.id]: i + 1 }))}
                               data-testid={`prom-${item.id}-${i + 1}`}/>
                        <div className="font-bold mb-0.5">{i + 1}</div>
                        <div className="text-[10px] leading-tight">{opt}</div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {options.map((opt, i) => (
                      <label key={i} className={`block p-2.5 rounded-md border cursor-pointer transition text-sm ${answers[item.id] === i ? 'bg-brand/10 border-brand' : 'border-soft hover:border-brand/40'}`}>
                        <input type="radio" name={item.id} className="mr-2"
                               checked={answers[item.id] === i}
                               onChange={() => setAnswers(a => ({ ...a, [item.id]: i }))}
                               data-testid={`prom-${item.id}-${i}`}/>
                        {opt}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex justify-end pt-2">
            <button onClick={submit}
                    disabled={submitting || Object.keys(answers).length === 0}
                    className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="prom-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Activity className="w-4 h-4"/>}
              {submitting ? 'Scoring…' : 'Submit & get score'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
